import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CustomerProfile, FormType, LoyaltyInterruptModal } from '@/models';

import { swiper, swiperSlide, SwiperComponent } from 'vue-awesome-swiper';
import { SwiperOptions } from 'swiper';
import { Routes } from '@/router';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { isAutoSubscribesToMarketingEmail } from '@/helpers';
import { MUTATIONS } from '@/store/reward';

export enum LoyaltyExplainerModalState {
  WelcomeUnenrolled = 'welcome-unenrolled',
  Explainer = 'explainer',
  Declined = 'declined',
  RewardsGeneralOnboarding = 'rewards-general-onboarding',
  Extras = 'extras',
}

@Component({
  name: 'CmgLoyaltyExplainerModal',
  components: {
    swiper,
    swiperSlide,
  },
})
export default class CmgLoyaltyExplainerModal extends Vue {
  explainerSwiper: SwiperComponent | null = null;
  swiperOptions: SwiperOptions = {
    pagination: {
      el: '.swiper-pagination',
    },
    on: {
      slideNextTransitionStart: () => {
        this.sendModalNextAnalyticsEvent();
      },
      slidePrevTransitionStart: () => {
        this.sendModalPreviousAnalyticsEvent();
      },
    },
  };

  isJoiningRewards = false;
  backgroundColor = 'white';

  get visible() {
    const visible = this.$appStore.state.ui.modals.loyaltyExplainer.visible;
    if (visible) {
      this.sendModalViewAnalyticsEvent();
    }

    return visible;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get isLoggedIn(): boolean {
    return !!this.$services.customer.isLoggedIn();
  }

  get logos() {
    return this.$appStore.state.cms.images.logos;
  }

  get legalCopy(): string {
    return this.$services.config.getLegalText().loggedInUnEnrolled;
  }

  get badgeImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionRewardShout.default;
  }

  get welcomeUnenrolledBadgeImageUrl(): string {
    return this.$services.config.getLoyaltyInterruptModal()?.medallionImageUrl ?? '';
  }

  get bagMedallionImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionBag.default;
  }

  get scanMedallionImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionScanNew.default;
  }

  get bonusBadgeImageUrl(): string {
    return this.logos.medallionBonusNew.default;
  }

  get celebrateBadgeImageUrl(): string {
    return this.logos.medallionCelebrateNew.default;
  }

  get extrasMedallionImageUrl(): string {
    return this.$appStore.state.cms.images.logos.medallionExtras.default;
  }

  get threepeatBadgeImageUrl(): string {
    return this.$appStore.state.cms.images.logos.badgeThreepeat.default;
  }

  get planetFriendlyBadgeImageUrl(): string {
    return this.$appStore.state.cms.images.logos.badgePlanetFriendly.default;
  }

  get foundingMemberBadgeImageUrl(): string {
    return this.$appStore.state.cms.images.logos.badgeFoundingMember.default;
  }

  get milestonesImageUrl(): string {
    return this.$appStore.state.cms.images.backgrounds.milestones.default;
  }

  get loyaltyInterruptModal(): LoyaltyInterruptModal | undefined {
    return this.$services.config.getLoyaltyInterruptModal();
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isUnenrolled(): boolean {
    return !this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get firstName(): string {
    return (this.customer && this.customer.firstName) || '';
  }

  get isEnrolledInLoyalty(): boolean {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get isGuest(): boolean {
    return !this.customer || this.customer.isGuest;
  }

  get isGuacMode(): boolean {
    return this.$appStore.getters.isCustomerGuacMode;
  }

  get args() {
    return this.$appStore.state.ui.modals.loyaltyExplainer.args;
  }

  get state(): string {
    return (this.args && this.args.state) || '';
  }

  get atFirstSlide(): boolean | null {
    if (!this.explainerSwiper || !this.explainerSwiper.swiper) {
      return null;
    }

    return this.explainerSwiper.swiper.isBeginning;
  }

  get atEndSlide(): boolean | null {
    if (!this.explainerSwiper || !this.explainerSwiper.swiper) {
      return null;
    }

    return this.explainerSwiper.swiper.isEnd;
  }

  get swiperRealIndex(): number | undefined {
    return this.explainerSwiper?.swiper?.realIndex;
  }

  get hasDeclinedLoyalty(): boolean {
    return this.$appStore.state.customer.loyalty.declined;
  }

  @Watch('state', { immediate: true })
  onStateChanged(state: LoyaltyExplainerModalState) {
    this.$nextTick(() => {
      const refStr = this.getRefStr(state);
      if (state === LoyaltyExplainerModalState.Extras) {
        this.backgroundColor = 'grey-medium';
      }
      const swiperRef = this.$refs[refStr] as SwiperComponent | undefined;

      if (swiperRef) {
        this.explainerSwiper = swiperRef;
      }
    });
  }

  getRefStr(state: LoyaltyExplainerModalState): string {
    switch (state) {
      case LoyaltyExplainerModalState.Explainer:
        return 'explainer-swiper';
      case LoyaltyExplainerModalState.Extras:
        return 'extras-swiper';
      default:
        return 'onboarding-swiper';
    }
  }

  closeModal() {
    this.sendModalClosedAnalyticsEvent();
    this.$services.ui.setModalVisibility('loyaltyExplainer', false);
    this.$services.ui.focusBack();
  }

  onClose(sendAnalytics: boolean = true) {
    if (sendAnalytics) {
      this.sendModalClosedAnalyticsEvent();
    }

    this.$services.loyalty.declineLoyalty();

    this.$services.ui.setModalVisibility('loyaltyExplainer', false);
    if (this.$appStore.state.ui.bag.visible) {
      this.$services.ui.nextFocus('pickup bag');
    } else {
      this.$services.ui.focusBack();
    }
  }

  onClickJoinNow() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsHowOnboard,
      action: AnalyticsEventAction.JoinNowClick,
      label: AnalyticsEventLabel.HowRewardsWorkJoinNowClick,
    });
    const enrollmentOptions: EnrollmentOptions = { customer: this.customer };
    this.$services.loyalty.enrollInLoyalty(enrollmentOptions)
      .then(() => this.$services.ui.setModalVisibility('subscribeEmailMarketing', !isAutoSubscribesToMarketingEmail(this.customer?.country)))
      .catch(e => this.$services.logger.error('Something went wrong enrolling in loyalty', undefined, e));
    this.onClose(false);
  }

  onClickSignIn(event: any) {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsHowOnboard,
      action: AnalyticsEventAction.HowRewardsWorkSignInClick,
      label: AnalyticsEventLabel.HowRewardsWorkSignInClick,
    });
    this.onClose(false);
    this.$services.ui.setModalVisibility('signIn', true, { type: FormType.SignIn });
  }

  onClickCreateAccount(event: any) {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsHowOnboard,
      action: AnalyticsEventAction.HowRewardsWorkCreateAccountClick,
      label: AnalyticsEventLabel.HowRewardsWorkCreateAccountClick,
    });
    this.onClose(false);
    this.$services.ui.setModalVisibility('signIn', true, { type: FormType.CreateAccount });
  }

  onClickViewRewardsExchange(event: any) {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardGeneralOnboard,
      action: AnalyticsEventAction.ViewRewardExchangeCtaClick,
      label: AnalyticsEventLabel.ViewRewardExchangeClick,
    });
    this.onClose(false);
    this.$router.push({ name: Routes.RewardsExchange });
  }

  goToPreviousExplainerSlide() {
    if (!this.explainerSwiper || !this.explainerSwiper.swiper) {
      return;
    }

    this.explainerSwiper.swiper.slidePrev();
    // set timeout equal to transition duration so transition is smooth and uninterrupted
    setTimeout(() => this.$nextTick(() => this.$services.ui.focusDiv('.content-root')), 300);
  }

  goToNextExplainerSlide() {
    if (!this.explainerSwiper || !this.explainerSwiper.swiper) {
      return;
    }

    this.explainerSwiper.swiper.slideNext();
    // set timeout equal to transition duration so transition is smooth and uninterrupted
    setTimeout(() => this.$nextTick(() => this.$services.ui.focusDiv('.content-root')), 300);
  }

  goToExplanationState() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsNonLoyaltyWelcome,
      action: AnalyticsEventAction.HowRewardsWorkCtaClick,
      label: AnalyticsEventLabel.RewardsNonLoyaltyHowRewardsWorkClick,
    });
    this.goToState(LoyaltyExplainerModalState.Explainer);
    this.$nextTick(() => this.$services.ui.focusDiv('.content-root'));
  }

  goToDeclinedState() {
    this.$services.loyalty.declineLoyalty();

    this.goToState(LoyaltyExplainerModalState.Declined);
  }

  goToOnboardingState() {
    this.goToState(LoyaltyExplainerModalState.RewardsGeneralOnboarding);

    if (this.customer) {
      this.customer.hasSeenRewardsGenOnboarding = true;
      this.$services.customer.updateCustomer({ hasSeenRewardsGenOnboarding: true });
    }

    this.$nextTick(() => this.$services.ui.focusDiv('.content-root'));
  }

  goToState(state: LoyaltyExplainerModalState) {
    if (!this.args) {
      return;
    }

    this.args.state = state;
  }

  onClickGetPoints() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardGeneralOnboard,
      action: AnalyticsEventAction.GetPointsClick,
      label: AnalyticsEventLabel.GetPointsClick,
    });
    this.$router.push({ name: Routes.RetroCredit });
    this.$services.ui.setModalVisibility('loyaltyExplainer', false);
  }

  sendModalClosedAnalyticsEvent() {
    switch (this.state) {
      case LoyaltyExplainerModalState.RewardsGeneralOnboarding:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardGeneralOnboard,
          action: AnalyticsEventAction.OnBoardingModalClose,
          label: AnalyticsEventLabel.OnBoardingModalViewClose,
        });
        break;
      case LoyaltyExplainerModalState.Explainer:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardsHowOnboard,
          action: AnalyticsEventAction.HowRewardsWorkModalClose,
          label: AnalyticsEventLabel.HowRewardsWorkModal,
        });
        break;
    }
  }

  sendModalViewAnalyticsEvent() {
    switch (this.state) {
      case LoyaltyExplainerModalState.RewardsGeneralOnboarding:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardGeneralOnboard,
          action: AnalyticsEventAction.OnBoardingModalView,
          label: AnalyticsEventLabel.OnBoardingModalViewClose,
        });
        break;
      case LoyaltyExplainerModalState.Explainer:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardsHowOnboard,
          action: AnalyticsEventAction.HowRewardsWorkModalView,
          label: AnalyticsEventLabel.HowRewardsWorkModal,
        });
        break;
    }
  }

  sendModalNextAnalyticsEvent() {
    switch (this.state) {
      case LoyaltyExplainerModalState.RewardsGeneralOnboarding:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardGeneralOnboard,
          action: AnalyticsEventAction.NextClick,
          label: AnalyticsEventLabel.GeneralOnboardingNextClick,
        });
        break;
      case LoyaltyExplainerModalState.Explainer:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardsHowOnboard,
          action: AnalyticsEventAction.NextClick,
          label: AnalyticsEventLabel.HowRewardsWorkNextClick,
        });
        break;
    }
  }

  sendModalPreviousAnalyticsEvent() {
    switch (this.state) {
      case LoyaltyExplainerModalState.RewardsGeneralOnboarding:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardGeneralOnboard,
          action: AnalyticsEventAction.PreviousClick,
          label: AnalyticsEventLabel.GeneralOnboardingPreviousClick,
        });
        break;
      case LoyaltyExplainerModalState.Explainer:
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.RewardsHowOnboard,
          action: AnalyticsEventAction.PreviousClick,
          label: AnalyticsEventLabel.HowRewardsWorkPreviousClick,
        });
        break;
    }
  }

  async onClickJoinRewards() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsNonLoyaltyWelcome,
      action: AnalyticsEventAction.JoinNowClick,
      label: AnalyticsEventLabel.RewardsNonLoyaltyJoinNowClick,
    });
    try {
      this.isJoiningRewards = true;
      const enrollmentOptions: EnrollmentOptions = { customer: this.customer, pageTitle: document.title };
      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);

      this.goToOnboardingState();

      this.$appStore.commit(MUTATIONS.SET_UNREACHABLE, false);
    } catch (e: any) {
      this.$services.logger.error('Something went wrong joining rewards', { error: e });
    } finally {
      this.isJoiningRewards = false;
    }
  }

  declineLoyalty() {
    this.$services.loyalty.declineLoyalty();
  }
}
